import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { marginCalls } from "@/api/funding";
import { Badge, Card, CardBody, EmptyState, Select } from "@/components/ui";

const FILTERS: { value: "" | "CALL" | "STOP_OUT"; label: string }[] = [
  { value: "", label: "All events" },
  { value: "CALL", label: "Margin calls" },
  { value: "STOP_OUT", label: "Stop-outs" },
];

/**
 * Margin-call / stop-out audit trail. CALL = account crossed the margin-call
 * level; STOP_OUT = positions force-liquidated at the stop-out level.
 * Read-only operator view for risk oversight.
 */
export function AdminMarginCallsPage() {
  const [action, setAction] = useState<"" | "CALL" | "STOP_OUT">("");
  const list = useQuery({
    queryKey: ["margin-calls", action],
    queryFn: () => marginCalls.list(action || undefined),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Margin calls &amp; stop-outs</h1>
        <div className="w-44">
          <Select value={action} onChange={(e) => setAction(e.target.value as "" | "CALL" | "STOP_OUT")}>
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState title="No events" description="Margin-call and stop-out events appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Time</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Event</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Account</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Level %</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Equity</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Margin used</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Free margin</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((m) => (
                  <tr key={m.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {m.created_at ? new Date(m.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge tone={m.action === "STOP_OUT" ? "bad" : "warn"}>{m.action}</Badge>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {m.trading_account_id.slice(0, 8)}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{m.level_pct}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{m.equity}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{m.margin_used}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{m.free_margin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
